import {Injectable} from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class SvgDownloadService {
  scale = 3;

  async convertTo(
    svg: SVGSVGElement | HTMLElement,
    type: 'image/png' | 'image/jpeg' | 'image/svg+xml',
    jpegQuality = 1
  ) {
    if (!svg) {
      throw new Error('No SVG Element');
    }

    this.copySelectedStyles(svg, ['text']);

    if (type === 'image/svg+xml') {
      return this.getSvgBlobUrl(svg);
    }

    const imageUrl = await this.getImageBlobUrl(svg, type, jpegQuality);
    return imageUrl;
  }

  private getImageBlobUrl(
    svg: SVGSVGElement | HTMLElement,
    type: 'image/png' | 'image/jpeg',
    jpegQuality: number
  ): Promise<string> {
    return new Promise((resolve, reject) => {
      const svgString = new XMLSerializer().serializeToString(svg);
      const scaledSvgString = this.createScaledSvg(
        svgString,
        this.getScaleWidth(svg),
        this.getScaleHeight(svg)
      );
      const svgBlob = new Blob([scaledSvgString], {
        type: 'image/svg+xml;charset=utf-8'
      });
      const blobUrl = URL.createObjectURL(svgBlob);

      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = this.getScaleWidth(svg);
        canvas.height = this.getScaleHeight(svg);

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);

        try {
          const dataUrl = canvas.toDataURL(type, jpegQuality);
          resolve(dataUrl);
        } catch (error: unknown) {
          reject(error);
        }

        URL.revokeObjectURL(blobUrl);
        canvas.remove();
      };
      img.onerror = e => {
        console.error('Unbekannter Fehler beim Laden des Bildes', e);
      };
      img.src = blobUrl;
    });
  }

  private getSvgBlobUrl(svg: SVGSVGElement | HTMLElement): string {
    const xml = new XMLSerializer().serializeToString(svg);
    const svg64 = btoa(unescape(encodeURIComponent(xml)));
    const b64start = 'data:image/svg+xml;base64,';
    return b64start + svg64;
  }

  private copySelectedStyles(
    svg: SVGSVGElement | HTMLElement,
    selectors: string[]
  ) {
    for (const selector of selectors) {
      const elements = Array.from(svg.querySelectorAll(selector));
      for (const el of elements) {
        (el as HTMLElement).style.fontSize =
          window.getComputedStyle(el).fontSize;
        (el as HTMLElement).style.fontFamily =
          window.getComputedStyle(el).fontFamily;
        (el as HTMLElement).style.lineHeight =
          window.getComputedStyle(el).lineHeight;
        (el as HTMLElement).style.wordWrap =
          window.getComputedStyle(el).wordWrap;
        (el as HTMLElement).style.fill = window.getComputedStyle(el).fill;
        (el as HTMLElement).style.fontWeight =
          window.getComputedStyle(el).fontWeight;
        (el as HTMLElement).style.textAnchor =
          window.getComputedStyle(el).textAnchor;
        (el as HTMLElement).style.textAlign =
          window.getComputedStyle(el).textAlign;
        (el as HTMLElement).style.dominantBaseline =
          window.getComputedStyle(el).dominantBaseline;
        (el as HTMLElement).style.color = window.getComputedStyle(el).color;
      }
    }
  }

  private createScaledSvg(
    xml: string,
    newWidth: number,
    newHeight: number
  ): string {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, prefer-named-capture-group
    const width = /<svg[^>]*width="([^"]+)"/.exec(xml)![1];
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, prefer-named-capture-group
    const height = /<svg[^>]*height="([^"]+)"/.exec(xml)![1];

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, prefer-named-capture-group
    xml = xml.replace(/(<svg[^>]*width=")[^"]+(")/, `$1${newWidth}$2`);
    xml = xml.replace(
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, prefer-named-capture-group
      /(<svg[^>]*height=")[^"]+(")/,
      `$1${newHeight}$2`
    );
    xml = xml.replace(
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, prefer-named-capture-group
      /(<svg[^>]*viewBox=")[^"]+(")/,
      `$1 0 0 ${width} ${height}$2`
    );
    return xml;
  }

  private getScaleWidth(svg: SVGSVGElement | HTMLElement) {
    return parseInt(svg.getAttribute('width') ?? '0', 10) * this.scale;
  }

  private getScaleHeight(svg: SVGSVGElement | HTMLElement) {
    return parseInt(svg.getAttribute('height') ?? '0', 10) * this.scale;
  }
}
